import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  clearPlatformAdminToken,
  getPlatformAdminToken,
  platformAdminApi,
  setPlatformAdminToken,
  type StudioProcessedNudge,
} from '../lib/api'

const CATEGORIES = ['leadership', 'inclusion', 'burnout', 'safety', 'recognition', 'goals'] as const

type StagedRow = StudioProcessedNudge & {
  clientKey: string
  showConflict: boolean
  /** When false, card shows a compact summary; Edit expands fields. */
  editing: boolean
}

function cloneForStudioRaw(row: StagedRow): Record<string, unknown> {
  const { clientKey: _c, showConflict: _s, editing: _ed, existing_nudge: _e, ...rest } = row
  return { ...rest }
}

export function NudgeStudioPage() {
  const [tokenInput, setTokenInput] = useState('')
  const [authed, setAuthed] = useState(!!getPlatformAdminToken())
  const [briefing, setBriefing] = useState('')
  const [staged, setStaged] = useState<StagedRow[]>([])
  const [loading, setLoading] = useState(false)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lastProvider, setLastProvider] = useState<'claude' | 'workers_ai' | null>(null)
  const [briefingWasTruncated, setBriefingWasTruncated] = useState(false)

  const login = (e: React.FormEvent) => {
    e.preventDefault()
    setPlatformAdminToken(tokenInput.trim())
    setAuthed(true)
    setTokenInput('')
    setError(null)
  }

  const logout = () => {
    clearPlatformAdminToken()
    setAuthed(false)
    setStaged([])
    setBriefing('')
    setLastProvider(null)
    setBriefingWasTruncated(false)
  }

  const processBriefing = useCallback(async () => {
    const text = briefing.trim()
    if (!text) {
      setError('Paste a briefing first.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const r = await platformAdminApi.processBriefing(text)
      setLastProvider(r.llm_provider)
      setBriefingWasTruncated(r.briefing_truncated)
      setStaged(
        r.nudges.map((n) => ({
          ...n,
          clientKey: crypto.randomUUID(),
          showConflict: false,
          editing: false,
        }))
      )
      if (!r.nudges.length) {
        setError('No nudges were extracted. Try a longer briefing or different wording.')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Process failed')
    } finally {
      setLoading(false)
    }
  }, [briefing])

  const updateRow = (key: string, patch: Partial<StagedRow>) => {
    setStaged((rows) => rows.map((r) => (r.clientKey === key ? { ...r, ...patch } : r)))
  }

  const discardRow = (key: string) => {
    setStaged((rows) => rows.filter((r) => r.clientKey !== key))
  }

  const saveRow = async (row: StagedRow) => {
    setSavingKey(row.clientKey)
    setError(null)
    try {
      await platformAdminApi.saveStudioNudge({
        title: row.title,
        category: row.category,
        science_source: row.science_source,
        insight: row.insight,
        nudge_text: row.nudge_text,
        timing_cue: row.timing_cue,
        system_target: row.system_target,
        effectiveness_score: row.effectiveness_score,
        channel: 'any',
        studio_raw: cloneForStudioRaw(row),
      })
      discardRow(row.clientKey)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSavingKey(null)
    }
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-warm-base">
        <form onSubmit={login} className="w-full max-w-md space-y-4 bg-warm-card border border-warm-border rounded-2xl p-8 shadow-card">
          <h1 className="text-xl font-bold text-ink">Nudge Studio</h1>
          <p className="text-sm text-ink-muted">
            Platform admin token required (same as{' '}
            <Link to="/admin" className="text-brand-teal underline">
              /admin
            </Link>
            ).
          </p>
          <input
            type="password"
            autoComplete="off"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            className="w-full bg-warm-inset border border-warm-border rounded-lg px-3 py-2 text-sm"
            placeholder="Platform admin token"
          />
          <button
            type="submit"
            className="w-full py-2 rounded-full text-white text-sm font-semibold"
            style={{ background: '#2A7A8A' }}
          >
            Continue
          </button>
        </form>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-warm-base p-4 md:p-8 text-ink">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Nudge Studio
            </h1>
            <p className="text-sm text-ink-muted mt-1">
              Parse briefings → stage → save to pending review (
              <Link to="/admin" className="text-brand-teal underline">
                Platform admin
              </Link>
              ).
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={logout}
              className="text-sm text-ink-muted underline"
            >
              Clear token
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm px-4 py-2">{error}</div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <section className="bg-warm-card border border-warm-border rounded-2xl p-5 shadow-card flex flex-col min-h-[320px]">
            <h2 className="text-sm font-semibold text-ink mb-2">Gemini briefing</h2>
            <p className="text-xs text-ink-muted mb-3">Paste the raw daily briefing text.</p>
            <textarea
              value={briefing}
              onChange={(e) => setBriefing(e.target.value)}
              placeholder="Paste briefing…"
              rows={14}
              className="flex-1 w-full min-h-[220px] bg-warm-inset border border-warm-border rounded-xl px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand-teal resize-y"
            />
            <button
              type="button"
              disabled={loading || !briefing.trim()}
              onClick={() => void processBriefing()}
              className="mt-4 py-2.5 rounded-full text-white text-sm font-semibold disabled:opacity-50 shadow-glow-teal"
              style={{ background: '#2A7A8A' }}
            >
              {loading ? 'Processing…' : 'Process briefing'}
            </button>
            <p className="mt-2 text-[11px] text-ink-muted">
              Uses Cloudflare Workers AI by default. If <code className="text-xs bg-warm-inset px-1 rounded">CLAUDE_API_KEY</code>{' '}
              is set on the Worker, Anthropic Claude is used instead.
            </p>
          </section>

          <section className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-ink">Staging area</h2>
              <div className="flex flex-wrap items-center gap-2">
                {lastProvider && (
                  <span
                    className="text-[11px] px-2 py-0.5 rounded-full border border-warm-border bg-warm-inset text-ink-muted"
                    title="LLM used for this run"
                  >
                    {lastProvider === 'claude' ? 'Anthropic Claude' : 'Workers AI (Llama 8B)'}
                  </span>
                )}
                <span className="text-xs text-ink-muted">
                  {staged.length} item{staged.length === 1 ? '' : 's'}
                </span>
              </div>
            </div>
            {briefingWasTruncated && (
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Only the first 32,000 characters were sent to Workers AI. To parse the full briefing, add{' '}
                <code className="text-[11px]">CLAUDE_API_KEY</code> (Anthropic API credits required), or paste a shorter
                briefing.
              </p>
            )}
            {staged.length === 0 ? (
              <div className="bg-warm-card border border-dashed border-warm-border rounded-2xl p-8 text-center text-sm text-ink-muted">
                Process a briefing to see extracted nudges here.
              </div>
            ) : (
              <ul className="space-y-4">
                {staged.map((row) => {
                  const dup = row.is_potential_duplicate
                  const busy = savingKey === row.clientKey
                  return (
                    <li
                      key={row.clientKey}
                      className={`rounded-2xl border p-4 shadow-card ${
                        dup
                          ? 'border-amber-400 bg-amber-50/80'
                          : 'border-warm-border bg-warm-card'
                      }`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                        <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                          {dup ? 'Potential duplicate' : 'New'}
                        </span>
                        {dup && row.duplicate_reason && (
                          <span className="text-xs text-amber-900">{row.duplicate_reason}</span>
                        )}
                      </div>

                      {!row.editing ? (
                        <div className="text-sm space-y-2">
                          <p className="font-semibold text-ink">{row.title}</p>
                          <p className="text-xs text-ink-muted capitalize">{row.category}</p>
                          <p className="text-xs text-ink-muted">
                            <span className="font-medium text-ink-soft">Source:</span> {row.science_source}
                          </p>
                          <p className="text-ink line-clamp-3 whitespace-pre-wrap">{row.insight}</p>
                          <p className="text-ink-soft text-xs">
                            <span className="font-medium">Nudge:</span> {row.nudge_text.slice(0, 160)}
                            {row.nudge_text.length > 160 ? '…' : ''}
                          </p>
                          <p className="text-xs text-brand-teal font-medium">Timing: {row.timing_cue}</p>
                        </div>
                      ) : (
                        <div className="space-y-2 text-sm">
                          <label className="block">
                            <span className="text-xs text-ink-muted">Title</span>
                            <input
                              value={row.title}
                              onChange={(e) => updateRow(row.clientKey, { title: e.target.value })}
                              className="mt-0.5 w-full bg-warm-inset border border-warm-border rounded-lg px-2 py-1.5 text-sm"
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs text-ink-muted">Category</span>
                            <select
                              value={row.category}
                              onChange={(e) => updateRow(row.clientKey, { category: e.target.value })}
                              className="mt-0.5 w-full bg-warm-inset border border-warm-border rounded-lg px-2 py-1.5 text-sm"
                            >
                              {CATEGORIES.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="block">
                            <span className="text-xs text-ink-muted">Research source</span>
                            <input
                              value={row.science_source}
                              onChange={(e) => updateRow(row.clientKey, { science_source: e.target.value })}
                              className="mt-0.5 w-full bg-warm-inset border border-warm-border rounded-lg px-2 py-1.5 text-sm"
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs text-ink-muted">Insight</span>
                            <textarea
                              value={row.insight}
                              onChange={(e) => updateRow(row.clientKey, { insight: e.target.value })}
                              rows={3}
                              className="mt-0.5 w-full bg-warm-inset border border-warm-border rounded-lg px-2 py-1.5 text-sm resize-y"
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs text-ink-muted">Nudge text</span>
                            <textarea
                              value={row.nudge_text}
                              onChange={(e) => updateRow(row.clientKey, { nudge_text: e.target.value })}
                              rows={3}
                              className="mt-0.5 w-full bg-warm-inset border border-warm-border rounded-lg px-2 py-1.5 text-sm resize-y"
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs text-ink-muted">Timing cue</span>
                            <input
                              value={row.timing_cue}
                              onChange={(e) => updateRow(row.clientKey, { timing_cue: e.target.value })}
                              className="mt-0.5 w-full bg-warm-inset border border-warm-border rounded-lg px-2 py-1.5 text-sm"
                            />
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            <label className="block">
                              <span className="text-xs text-ink-muted">System</span>
                              <select
                                value={row.system_target}
                                onChange={(e) =>
                                  updateRow(row.clientKey, {
                                    system_target: e.target.value === 'system2' ? 'system2' : 'system1',
                                  })
                                }
                                className="mt-0.5 w-full bg-warm-inset border border-warm-border rounded-lg px-2 py-1.5 text-sm"
                              >
                                <option value="system1">system1</option>
                                <option value="system2">system2</option>
                              </select>
                            </label>
                            <label className="block">
                              <span className="text-xs text-ink-muted">Score</span>
                              <input
                                type="number"
                                step="0.01"
                                min={0.35}
                                max={0.95}
                                value={row.effectiveness_score}
                                onChange={(e) =>
                                  updateRow(row.clientKey, {
                                    effectiveness_score: Number.parseFloat(e.target.value) || 0.65,
                                  })
                                }
                                className="mt-0.5 w-full bg-warm-inset border border-warm-border rounded-lg px-2 py-1.5 text-sm"
                              />
                            </label>
                          </div>
                        </div>
                      )}

                      {dup && row.duplicate_of_id && (
                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={() =>
                              updateRow(row.clientKey, { showConflict: !row.showConflict })
                            }
                            className="text-xs font-medium text-amber-900 underline"
                          >
                            {row.showConflict ? 'Hide conflict' : 'View conflict'}
                          </button>
                          {row.showConflict && (
                            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                              <div className="rounded-lg border border-warm-border bg-warm-inset p-3">
                                <p className="font-semibold text-ink mb-2">Proposed</p>
                                <p className="font-medium text-ink">{row.title}</p>
                                <p className="text-ink-muted mt-1 whitespace-pre-wrap">
                                  **Insight:** {row.insight}
                                  {'\n\n'}
                                  **Nudge:** {row.nudge_text}
                                  {'\n\n'}
                                  **Timing:** {row.timing_cue}
                                </p>
                              </div>
                              <div className="rounded-lg border border-warm-border bg-white p-3">
                                <p className="font-semibold text-ink mb-2">Existing library</p>
                                {row.existing_nudge ? (
                                  <>
                                    <p className="font-medium text-ink">{row.existing_nudge.title}</p>
                                    <p className="text-ink-muted mt-1 text-[11px]">{row.existing_nudge.category}</p>
                                    <p className="mt-2 whitespace-pre-wrap text-ink">{row.existing_nudge.body}</p>
                                    {row.existing_nudge.science_source && (
                                      <p className="mt-2 text-ink-muted">Source: {row.existing_nudge.science_source}</p>
                                    )}
                                  </>
                                ) : (
                                  <p className="text-ink-muted">
                                    Template id <code className="text-ink">{row.duplicate_of_id}</code> (reload process
                                    briefing if details are missing).
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => updateRow(row.clientKey, { editing: !row.editing })}
                          className="px-4 py-2 rounded-full border border-warm-border text-xs font-medium text-ink hover:bg-warm-inset"
                        >
                          {row.editing ? 'Collapse' : 'Edit'}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void saveRow(row)}
                          className="px-4 py-2 rounded-full text-white text-xs font-semibold disabled:opacity-50"
                          style={{ background: '#2A7A8A' }}
                        >
                          {busy ? 'Saving…' : 'Save to pending review'}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => discardRow(row.clientKey)}
                          className="px-4 py-2 rounded-full border border-warm-border text-xs font-medium text-ink-muted hover:bg-warm-inset"
                        >
                          Discard
                        </button>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
